import React from 'react';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { useShallow } from 'zustand/react/shallow';
import * as authApi from '../api/auth-api';
import type { MeResponse } from '../api/auth-api';
import { useAuthStore } from './useAuth';

// Bootstraps the session from `/v2/admins/authorize` whenever permissions aren't loaded yet.
// Two entry points hit this:
//   1. Fresh page load (hard refresh, new tab) — `step !== 'authenticated'`. The access_token
//      cookie keeps CookiesGuard happy, so /authorize works without an in-memory bearer.
//   2. Just after login — `step === 'authenticated'` but `permissionsLoaded === false`. The
//      login response only carries user+apps, not scopes/flags/roleKey, so we fetch them here
//      before AuthGuard releases the gated page.
//
// Note: cookie-driven restore is only valid while the access cookie is alive (24h). Beyond
// that we would need /v2/authentication/refresh to succeed without a gamix-app-id header,
// which is a separate backend change.
export function useSessionBootstrap(): UseQueryResult<MeResponse, Error> {
  const {
    step, permissionsLoaded, activeAppId, authenticate, setPermissions,
  } = useAuthStore(
    useShallow((s) => ({
      step: s.step,
      permissionsLoaded: s.permissionsLoaded,
      activeAppId: s.activeApp?.id ?? null,
      authenticate: s.authenticate,
      setPermissions: s.setPermissions,
    })),
  );

  /* Keying on `activeAppId` makes a workspace switch surface as a new query rather than
   * returning the previous workspace's cached scopes (staleTime: Infinity would otherwise
   * suppress the refetch). */
  const query = useQuery({
    queryKey: ['session-bootstrap', activeAppId],
    queryFn: () => authApi.fetchMe('corePilot'),
    enabled: step !== 'authenticated' || !permissionsLoaded,
    retry: false,
    staleTime: Infinity,
    /* Bound the per-workspace cache so a user with N workspaces doesn't accumulate N entries
     * indefinitely. 30m drops scopes the user is no longer in while still surviving normal
     * tab-switching idle. */
    gcTime: 1000 * 60 * 30,
  });

  /* Mirror the response into the auth store via effect (rather than from inside queryFn) so
   * the cache-hit path is covered too: switching back to a previously visited workspace
   * returns React Query's cache without re-running queryFn, but `permissionsLoaded` still
   * has to flip back to true or AuthGuard never releases.
   *
   * The hook is mounted in two places (SessionBootstrapper + AuthGuard) and shares a query
   * cache, so both consumers' effects fire when `data` arrives. We dedup by reading
   * `permissionsLoaded` synchronously: the first effect applies, flips the flag, and the
   * second effect bails. */
  const data = query.data;
  React.useEffect(() => {
    if (!data) return;
    if (useAuthStore.getState().permissionsLoaded) return;
    authenticate(
      {
        id: data.admin.id,
        email: data.admin.email,
        firstName: data.admin.firstName,
        lastName: data.admin.lastName,
        role: data.admin.role ?? null,
      },
      data.authorizedApps.map((a) => ({
        id: a.id, app: a.app, role: a.role, appType: a.appType,
      })),
    );
    setPermissions(data.scopes ?? [], data.featureFlags ?? [], data.roleKey ?? null);
  }, [data, authenticate, setPermissions]);

  return query;
}
